// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 4, Iter 332).
// Rendert einen einzelnen Termin als Stapel von Sub-Slots:
//   - Belegt → <ParticipantSlot>
//   - Durch Mindbody blockiert → <MindbodyBlockedSlot>
//   - Frei → <EmptyParticipantSlot>
// Plus dahinter (sofern Trainer NICHT in Ferien): einen optischen „+ Nächsten
// Termin buchen"-Button, der die Lücke nach dem Termin anzeigt und auf das
// Slot-Raster snappt.
//
// Verhalten ist identisch zum vorherigen Inline-Code in der DayView.

import React from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { getSwissHM, toLocalYMD } from './calendarUtils';
import {
  ParticipantSlot,
  MindbodyBlockedSlot,
  EmptyParticipantSlot,
} from './AppointmentSubSlots';
import {
  getValidSlotStartsForTrainer as utilGetValidSlotStartsForTrainer,
  getDensityAwareStep as utilGetDensityAwareStep,
} from '@/utils/trainerSlotLogic';

// 09/2026: Auf Wunsch des Studios ausgeblendet — der optische „+ Nächsten
// Termin buchen"-Schnellzugriff überall im Kalender entfernt. Auf `true`
// setzen, um ihn wieder zu aktivieren. Termine bucht man weiterhin über Klick
// auf einen freien Slot bzw. den normalen Buchungsdialog.
const SHOW_QUICK_ADD_NEXT_APPOINTMENT = false;

export const AppointmentBlock = ({
  apt,
  trainerData,
  trainerAppointments,
  // Calendar-Layout
  slotHeight,
  calendarStartMinutes,
  currentDate,
  // Daten
  serviceMap,
  services,
  customers,
  mindbodySlotsByTrainer,
  movingAppointment,
  onMoveIntoAppointment,
  anamneseStatus,
  minServiceTotalDuration,
  // Helpers
  isTrainerOnVacation,
  getMaxGapMinutes,
  getAppointmentColor,
  findMindbodyMappedSlotAt,
  // DnD
  draggedParticipant,
  setDraggedParticipant,
  setDropTarget,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleParticipantDragStart,
  handleDragEnd,
  handleRightClick,
  // Setter (Booking-Dialog öffnen)
  setSelectedSlot,
  setSelectedTrainer,
  setBookingData,
  setCustomTime,
  setIsAddingParticipant,
  setAvailableServices,
  setBookingDialogOpen,
  setAppointments,
  // Fetcher
  fetchAppointments,
  fetchCustomers,
  fetchServices,
  fetchAvailableServices,
  // Density-Aware Config (07/2026, optional — default null)
  densityConfig = null,
  appointments = [],
  // Spalten-Layout für überlappende (parallele) Termine (default: volle Breite)
  colIndex = 0,
  colCount = 1,
  getSlotBlockReason,
}) => {
  const startDate = new Date(apt.start_time);
  const endDate = new Date(apt.end_time);
  // 10/2026 Resize-Konflikt: prüft live, ob [start, newEnd] den Trainer-Slot
  // blockiert (Pause, anderer Termin, Mindbody). apt selbst wird ausgenommen.
  const checkResizeConflict = (a, newEnd) => {
    if (typeof getSlotBlockReason !== 'function') return false;
    try {
      const svc = services?.find((s) => s.id === a.service_id);
      return !!getSlotBlockReason(a.trainer_id, new Date(a.start_time), newEnd, [a.id], {
        maxParticipants: svc?.max_participants || 1,
        occupancy: 1,
      });
    } catch (_e) {
      return false;
    }
  };

  // Position vom Kalender-Start (Schweizer Zeit verwenden, sonst UTC-Sprung)
  const sStart = getSwissHM(apt.start_time);
  const sEnd = getSwissHM(apt.end_time);
  const startMinutesFromMidnight = sStart.hour * 60 + sStart.minute;
  const minutesFromCalendarStart = startMinutesFromMidnight - calendarStartMinutes;

  const durationMinutes = (endDate - startDate) / (1000 * 60);
  const topPosition = (minutesFromCalendarStart / 30) * slotHeight;
  const height = (durationMinutes / 30) * slotHeight;

  const startTimeStr = `${String(sStart.hour).padStart(2, '0')}:${String(sStart.minute).padStart(2, '0')}`;
  const endTimeStr = `${String(sEnd.hour).padStart(2, '0')}:${String(sEnd.minute).padStart(2, '0')}`;

  // Teilnehmer-Daten
  const customerNames = apt.customer_names || (apt.customer_name ? [apt.customer_name] : []);
  const customerIds = apt.customer_ids || (apt.customer_id ? [apt.customer_id] : []);
  const customerPhones = apt.customer_phones || [];
  const customerSuitSizes = apt.customer_suit_sizes || [];
  const customerBlockFields = apt.customer_block_fields || [];
  const customerPhotoUrls = apt.customer_photo_urls || [];

  // EMS/Flex Erkennung (für Anzugsgrösse-Tooltip)
  const service = serviceMap.get(apt.service_id);
  const isEmsOrFlex =
    service?.name?.toLowerCase().includes('ems') ||
    service?.name?.toLowerCase().includes('flex') ||
    apt.service_name?.toLowerCase().includes('ems') ||
    apt.service_name?.toLowerCase().includes('flex');
  const maxParticipants = service?.max_participants || 1;

  // Mindbody-Überlappung (06/2026): MB-Direktbuchungen belegen Plätze der Gruppe
  const mbOverlapCount = (mindbodySlotsByTrainer[trainerData.trainer.id] || []).filter(
    (mb) => {
      if (mb.source_type !== 'appointment') return false;
      try {
        return new Date(mb.start_time) < endDate && new Date(mb.end_time) > startDate;
      } catch {
        return false;
      }
    }
  ).length;
  const customerCount = customerNames.length;
  const mbBlockedCount = Math.min(
    mbOverlapCount,
    Math.max(0, maxParticipants - customerCount)
  );

  // 06/2026 Fix (Sonja unsichtbar): Überbelegte Termine (mehr Teilnehmer als
  // max_participants, z.B. Service-Lookup-Fehler) nicht abschneiden
  const slotCount = Math.max(maxParticipants, customerNames.length);
  // Iter 09/2026 (Daniela versteckt): Überlappende parallele Termine desselben
  // Trainers werden in Spalten nebeneinander gelegt (colIndex/colCount), damit
  // keiner verdeckt wird. Bei colCount=1 (kein Overlap) unverändertes Verhalten.
  const colSpan = 100 / colCount;
  const colLeft = colIndex * colSpan;
  const widthPercentage = colSpan / slotCount;

  // ──────────────────────────────────────────────────────────────────
  //  Sub-Slots (Belegt / MB-Belegt / Frei)
  // ──────────────────────────────────────────────────────────────────
  const allSlots = [];

  // 06/2026 Fix (abgesagte Termine verdeckt): Ein abgesagter Termin, der
  // zeitlich einen AKTIVEN Termin desselben Trainers überlappt, verschwand
  // hinter dem aktiven Block (alle Slots haben zIndex 10). Lösung: solche
  // Absagen als gut sichtbaren grauen Streifen am rechten Rand ganz oben
  // rendern (zIndex 16). Nicht-überlappende Absagen bleiben als voller Block.
  const _aptCancelled = apt.status === 'cancelled' || apt.status === 'no_show';
  const _overlapsActive = _aptCancelled && (trainerAppointments || []).some((o) => {
    if (!o || o.id === apt.id) return false;
    if (o.status === 'cancelled' || o.status === 'no_show') return false;
    try {
      return new Date(o.start_time) < endDate && new Date(o.end_time) > startDate;
    } catch {
      return false;
    }
  });
  const _useCancelledStrip = _aptCancelled && _overlapsActive;
  const _notInformed = _aptCancelled && apt.customer_notified === false;
  if (_useCancelledStrip) {
    const _label = (customerNames && customerNames.length)
      ? customerNames.join(', ')
      : (apt.customer_name || 'Abgesagt');
    allSlots.push(
      <div
        key={`${apt.id}-cancelled-strip`}
        className="absolute rounded border border-zinc-400 bg-zinc-200/95 text-zinc-600 text-[9px] leading-tight px-1 py-0.5 overflow-hidden cursor-pointer flex items-center gap-1"
        style={{
          top: `${topPosition}px`,
          right: '1px',
          width: '30%',
          height: `${Math.max(height - 2, 16)}px`,
          zIndex: 16,
        }}
        title={`🚫 Abgesagt: ${_label}${_notInformed ? ' · 🔕 nicht informiert' : ''}`}
        data-testid={`cancelled-strip-${apt.id}`}
        onContextMenu={(e) => { e.preventDefault(); handleRightClick?.(e, apt, (customerIds || [])[0] || null); }}
        onClick={(e) => { e.stopPropagation(); handleRightClick?.(e, apt, (customerIds || [])[0] || null); }}
      >
        {_notInformed && (
          <span
            className="shrink-0 text-[8px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-0.5"
            data-testid={`not-informed-badge-${apt.id}`}
          >
            🔕
          </span>
        )}
        <span className="line-through truncate">🚫 {_label}</span>
      </div>
    );
  }

  for (let slotIdx = 0; !_useCancelledStrip && slotIdx < slotCount; slotIdx++) {
    const leftPosition = colLeft + (slotIdx / slotCount) * colSpan;
    const hasParticipant = slotIdx < customerNames.length;
    const isMbBlocked = !hasParticipant && slotIdx >= slotCount - mbBlockedCount;

    if (hasParticipant) {
      const _isCancelled = apt.status === 'cancelled' || apt.status === 'no_show';
      const name = _isCancelled
        ? `${apt.customer_notified === false ? '🔕 ' : ''}🚫 Abgesagt: ${customerNames[slotIdx]}`
        : customerNames[slotIdx];
      const customerId = customerIds[slotIdx];
      const participantAppointment =
        apt._participant_appointments?.[customerId] || apt;
      const phone = customerPhones[slotIdx] || '';
      const suitSize = customerSuitSizes[slotIdx];
      const hasFailedPayment = !!(apt.customer_failed_payments || [])[slotIdx];
      const consentMissing = !!(apt.customer_consents_missing || [])[slotIdx];
      const photoUrl = customerPhotoUrls[slotIdx];

      allSlots.push(
        <ParticipantSlot
          key={`tooltip-${apt.id}-${slotIdx}`}
          apt={participantAppointment}
          slotIdx={slotIdx}
          maxParticipants={maxParticipants}
          name={name}
          customerId={customerId}
          phone={phone}
          suitSize={suitSize}
          blockFields={customerBlockFields[slotIdx] || []}
          hasFailedPayment={hasFailedPayment}
          consentMissing={consentMissing}
          photoUrl={photoUrl}
          startTimeStr={startTimeStr}
          endTimeStr={endTimeStr}
          topPosition={topPosition}
          leftPosition={leftPosition}
          widthPercentage={widthPercentage}
          height={height}
          durationMinutes={durationMinutes}
          isEmsOrFlex={isEmsOrFlex}
          anamneseStatus={anamneseStatus}
          appointmentColor={getAppointmentColor(participantAppointment, [], {})}
          pushRelevant={!!service?.push_to_mindbody}
          onParticipantDragStart={handleParticipantDragStart}
          onDragEnd={handleDragEnd}
          onRightClick={handleRightClick}
          onResized={fetchAppointments}
          checkResizeConflict={checkResizeConflict}
          onMindbodyPushRetryDone={(updated) => {
            if (!updated) return;
            setAppointments((prev) =>
              prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
            );
          }}
        />
      );
    } else if (isMbBlocked) {
      allSlots.push(
        <MindbodyBlockedSlot
          key={`${apt.id}-mb-blocked-${slotIdx}`}
          apt={apt}
          slotIdx={slotIdx}
          maxParticipants={maxParticipants}
          topPosition={topPosition}
          leftPosition={leftPosition}
          widthPercentage={widthPercentage}
          height={height}
        />
      );
    } else if (apt.pending_partner?.name && slotIdx === customerNames.length) {
      // 08/2026: Partner eingeladen, Registrierung noch ausstehend
      allSlots.push(
        <div
          key={`${apt.id}-pending-partner-${slotIdx}`}
          className="absolute rounded border border-dashed border-amber-400 bg-amber-100/80 text-amber-800 text-[10px] leading-tight px-1 py-0.5 overflow-hidden"
          style={{
            top: `${topPosition}px`,
            left: `calc(${leftPosition}% + 1px)`,
            width: `calc(${widthPercentage}% - 2px)`,
            height: `${height - 2}px`,
            zIndex: 5,
          }}
          title={`Partner eingeladen: ${apt.pending_partner.name} — Registrierung ausstehend`}
          data-testid={`pending-partner-slot-${apt.id}`}
        >
          ⏳ {apt.pending_partner.name}
          <div className="font-semibold">Partner ausstehend</div>
        </div>
      );
    } else {
      allSlots.push(
        <EmptyParticipantSlot
          key={`${apt.id}-empty-${slotIdx}`}
          apt={apt}
          slotIdx={slotIdx}
          maxParticipants={maxParticipants}
          topPosition={topPosition}
          leftPosition={leftPosition}
          widthPercentage={widthPercentage}
          height={height}
          startTimeStr={startTimeStr}
          movingAppointment={movingAppointment}
          onMoveIntoAppointment={onMoveIntoAppointment}
          draggedParticipant={draggedParticipant}
          setDraggedParticipant={setDraggedParticipant}
          setDropTarget={setDropTarget}
          services={services}
          customers={customers}
          fetchAppointments={fetchAppointments}
          fetchCustomers={fetchCustomers}
          setSelectedSlot={setSelectedSlot}
          setSelectedTrainer={setSelectedTrainer}
          setBookingData={setBookingData}
          setCustomTime={setCustomTime}
          setIsAddingParticipant={setIsAddingParticipant}
          setAvailableServices={setAvailableServices}
          setBookingDialogOpen={setBookingDialogOpen}
        />
      );
    }
  }

  // 08/2026: Markierung „Verschiebung angefragt" (Tag-Verschiebung)
  if (apt.reschedule_requested) {
    allSlots.push(
      <div
        key={`${apt.id}-reschedule-badge`}
        className="absolute rounded-full bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 shadow"
        style={{ top: `${topPosition + 2}px`, right: '2px', zIndex: 20, pointerEvents: 'none' }}
        title={`Verschiebung angefragt${apt.reschedule_reason ? ` — ${apt.reschedule_reason}` : ''} (Kunde wurde benachrichtigt)`}
        data-testid={`reschedule-requested-badge-${apt.id}`}
      >
        ⇄
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────
  //  Quick-Add „Nächsten Termin buchen" (nur wenn Trainer NICHT in Ferien)
  // ──────────────────────────────────────────────────────────────────
  const trainerVacation = isTrainerOnVacation(trainerData.trainer.id);
  if (SHOW_QUICK_ADD_NEXT_APPOINTMENT && !trainerVacation) {
    const aptEndDate = new Date(apt.end_time);
    const endMinutes = aptEndDate.getMinutes();
    let nextSlotHours = aptEndDate.getHours();
    let nextSlotMinutes = endMinutes;

    // Round up to next 15-minute slot boundary
    const remainder = endMinutes % 15;
    if (remainder !== 0) {
      nextSlotMinutes = endMinutes + (15 - remainder);
      if (nextSlotMinutes >= 60) {
        nextSlotHours += 1;
        nextSlotMinutes -= 60;
      }
    }

    // 06/2026 Slot-Packing: Quick-Add muss ans Trainer-Slot-Raster snappen
    const _dateStrQA = toLocalYMD(currentDate);
    const _dayNameQA = currentDate
      .toLocaleDateString('en-US', { weekday: 'long' })
      .toLowerCase();
    const _validGrid = utilGetValidSlotStartsForTrainer(
      trainerData,
      _dateStrQA,
      _dayNameQA,
      utilGetDensityAwareStep(
        trainerData?.trainer?.id || trainerData?.id,
        currentDate,
        appointments,
        densityConfig
      )
    );
    const _rawMin = nextSlotHours * 60 + nextSlotMinutes;
    let _snapped = null;
    const _gridMinutes = Array.from(_validGrid)
      .map((t) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      })
      .sort((a, b) => a - b);
    for (const gm of _gridMinutes) {
      if (gm >= _rawMin) {
        _snapped = gm;
        break;
      }
    }
    if (_snapped !== null) {
      nextSlotHours = Math.floor(_snapped / 60);
      nextSlotMinutes = _snapped % 60;
    }

    const nextSlotTime = `${nextSlotHours.toString().padStart(2, '0')}:${nextSlotMinutes.toString().padStart(2, '0')}`;
    const _snapMinFromMidnight = nextSlotHours * 60 + nextSlotMinutes;
    const minutesFromCalendarEndForQuickAdd =
      _snapMinFromMidnight - calendarStartMinutes;
    const quickAddTopPosition = (minutesFromCalendarEndForQuickAdd / 30) * slotHeight;

    // Render nur wenn Platz vorhanden
    if (aptEndDate.getHours() < 20) {
      // Falls direkt nach dem Termin schon ein anderer beginnt: kein Quick-Add
      const hasImmediateNext = trainerAppointments.some((nextApt) => {
        const nextStart = new Date(nextApt.start_time);
        return (
          nextStart.getTime() === aptEndDate.getTime() ||
          (nextStart.getTime() > aptEndDate.getTime() &&
            nextStart.getTime() < aptEndDate.getTime() + 30 * 60000)
        );
      });

      if (!hasImmediateNext) {
        // Iter 318: "+" ausblenden wenn die Lücke kleiner ist als der
        // kürzeste verfügbare Service.
        let _quickAddBlocked = false;
        if (minServiceTotalDuration > 0) {
          try {
            const [qh, qm] = String(nextSlotTime).split(':');
            const qDate = new Date(currentDate);
            qDate.setHours(parseInt(qh, 10) || 0, parseInt(qm, 10) || 0, 0, 0);
            const gap = getMaxGapMinutes(trainerData.trainer.id, qDate);
            if (gap < minServiceTotalDuration) _quickAddBlocked = true;
          } catch {
            /* fall through */
          }
        }
        if (!_quickAddBlocked) {
          allSlots.push(
            <div
              key={`${apt.id}-quick-add`}
              className="absolute cursor-pointer flex items-center justify-center hover:bg-teal-100 transition-all border-2 border-teal-500 border-dashed rounded-lg drop-zone"
              data-testid={`quick-add-${apt.id}`}
              style={{
                top: `${quickAddTopPosition + 5}px`,
                left: '5%',
                width: '90%',
                height: `${slotHeight - 10}px`,
                backgroundColor: 'rgba(20, 184, 166, 0.1)',
                zIndex: 11,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSlot(nextSlotTime);
                setSelectedTrainer(trainerData);
                // Iter 324: Auto-Select des Service, falls Quick-Add neben
                // einem BELEGT-Mindbody-Slot mit Mapping liegt.
                const _mbMatch = findMindbodyMappedSlotAt(
                  trainerData.trainer.id,
                  nextSlotTime
                );
                const _prefillSvc = _mbMatch?.strongfit_service_id || '';
                setBookingData({
                  service_id: _prefillSvc,
                  customer_id: '',
                  notes: '',
                });
                setCustomTime(nextSlotTime);

                if (services.length === 0) fetchServices();
                if (customers.length === 0) fetchCustomers();
                // 08/2026: Services für den Quick-Add-Slot laden — vorher
                // blieb die Service-Liste leer/veraltet.
                if (typeof fetchAvailableServices === 'function') {
                  fetchAvailableServices(
                    trainerData.trainer.id,
                    toLocalYMD(currentDate),
                    nextSlotTime
                  );
                }

                if (_mbMatch && _prefillSvc) {
                  toast.info(
                    `Service vorausgewählt: ${_mbMatch.service_name || 'Mindbody-Gruppenslot'}`,
                    { duration: 2500 }
                  );
                }
                setBookingDialogOpen(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDragOver(e, trainerData, nextSlotTime);
              }}
              onDragLeave={handleDragLeave}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDrop(e, trainerData, nextSlotTime);
              }}
            >
              <Plus size={20} className="text-teal-600" />
              <div className="text-xs text-teal-700 ml-2">Nächsten Termin buchen</div>
            </div>
          );
        }
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────
  //  Stage 2 (Beauty): Einwirkzeit-Overlay (schraffiert)
  //  Behandlungsphasen mit staff_busy=false (Einwirken) werden als
  //  diagonaler Streifen über dem Terminbalken dargestellt — so sieht man
  //  aktive Arbeit vs. Einwirkzeit auf einen Blick. Nicht interaktiv.
  // ──────────────────────────────────────────────────────────────────
  const _phases = (service?.has_treatment_phases && Array.isArray(service?.treatment_phases))
    ? service.treatment_phases : [];
  if (!_useCancelledStrip && _phases.length > 0 && durationMinutes > 0) {
    const _pxPerMin = slotHeight / 30;
    let _cursor = 0;
    _phases.forEach((ph, i) => {
      const _dur = parseInt(ph.duration_minutes) || 0;
      if (ph.staff_busy === false && _dur > 0) {
        const _segTop = topPosition + _cursor * _pxPerMin;
        const _segH = _dur * _pxPerMin;
        allSlots.push(
          <div
            key={`${apt.id}-soak-${i}`}
            className="absolute rounded-sm overflow-hidden"
            style={{
              top: `${_segTop}px`,
              left: `calc(${colLeft}% + 1px)`,
              width: `calc(${colSpan}% - 2px)`,
              height: `${Math.max(_segH - 1, 2)}px`,
              zIndex: 14,
              pointerEvents: 'none',
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(255,255,255,0.55) 0px, rgba(255,255,255,0.55) 5px, rgba(148,163,184,0.35) 5px, rgba(148,163,184,0.35) 10px)',
              borderTop: '1px dashed rgba(100,116,139,0.6)',
              borderBottom: '1px dashed rgba(100,116,139,0.6)',
            }}
            title={`Einwirkzeit (${ph.name || 'Pause'}) — ${_dur} Min, Mitarbeiter frei`}
            data-testid={`soak-overlay-${apt.id}-${i}`}
          >
            {_segH >= 16 && (
              <span className="absolute left-1 top-0.5 text-[8px] font-semibold text-slate-600 leading-none">
                ⏳ Einwirken
              </span>
            )}
          </div>
        );
      }
      _cursor += _dur;
    });
  }

  return <>{allSlots}</>;
};

export default AppointmentBlock;
